import {
  FileFormatter,
  FileFormatterWithError,
} from '../interfaces/offerBulkImport';
export type HeaderMapConfig = Record<string, string>;

export const offerBulkHeaderMap: HeaderMapConfig = {
  ID_OFERTA_REFERÊNCIA: 'refOfferId',
  ID_PRODUTO: 'productSellGroupId',
  NOME: 'name',
  DESCRIÇÃO: 'description',
  VALOR: 'fullPrice',
  DATA_INÍCIO: 'startDate',
  DATA_FIM: 'endDate',
  PARCELAMENTO_MÁXIMO: 'installments',
  SUBSTITUIR_OFERTA_BASE: 'replaceDefaultOffer',
  OFERTA_RENOVAÇÃO: 'renewalOfferId',
  ID_MULTI_PERFIL: 'accessProfileId',
  DETALHAMENTO_CHECKOUT: 'disclaimer',
  VALOR_CHEIO_FAKE: 'displayPrice',
  ID_BUMP: 'orderBumpId',
  MOSTRAR_CUPOM: 'isDisplayCoupon',
  DIAS_GARANTIA: 'warrantyDays',
  ID_PROFILE_PROGRAMAS: 'profileId',
  ID_FLUXO_UP: 'flowId',
  RETIRAR_FALLBACK: 'onlyFull',
  HABILITAR_CARTÃO: 'isAvailableCreditCard',
  HABILITAR_NUPAY: 'isAvailableNuPay',
  HABILITAR_PAGALEVE: 'isAvailablePagaleve',
  HABILITAR_PIX: 'isAvailablePix',
  HABILITAR_BOLETO: 'isAvailableBillet',
  DESATIVAR_RENOVAÇÃO: 'renewalDisabled',
  ID_TIPO_AQUISIÇÃO: 'acquisitionTypeId',
  ID_SAZONALIDADE: 'seasonalityId',
  ID_TIPO: 'typeId',
  MULTI_OFERTA_TIPO_1: 'discountTypeValue1',
  MULTI_OFERTA_PERCENT_1: 'discountPercentageValue1',
  MULTI_OFERTA_TIPO_2: 'discountTypeValue2',
  MULTI_OFERTA_PERCENT_2: 'discountPercentageValue2',
};

function sanitizeHeader(header: string): string {
  return header.trim().replace(/[?*]$/, '');
}

/**
 * Faz split de CSV respeitando aspas e colunas vazias.
 */
export function parseCSVLine(line: string): string[] {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      values.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  values.push(current.trim());
  return values;
}

export function parseOfferBulkCSVRows(offersBulkCSV: string): {
  rows: FileFormatter[];
  rowLineNumbers: number[];
  errors: FileFormatterWithError[];
} {
  const lines = offersBulkCSV.split('\n').map(line => line.replace('\r', ''));

  const notEmptyLines = lines.filter(line => line.trim().length > 0);
  if (notEmptyLines.length < 2) {
    return { rows: [], rowLineNumbers: [], errors: [] };
  }

  const headers = parseCSVLine(notEmptyLines[0]).map(sanitizeHeader);
  const rows: FileFormatter[] = [];
  const rowLineNumbers: number[] = [];
  const errors: FileFormatterWithError[] = [];

  notEmptyLines.slice(1).forEach((line, index) => {
    const values = parseCSVLine(line);
    const row: FileFormatter = {};

    headers.forEach((header, headerIndex) => {
      row[header] = values[headerIndex] ?? '';
    });

    if (!Object.values(row).some(value => value !== '')) {
      return;
    }

    rows.push(row);
    rowLineNumbers.push(index + 2);

    if (values.length > headers.length) {
      errors.push({
        ...row,
        line: index + 2,
        field: 'CSV',
        messageError: 'Linha com mais colunas do que o cabeçalho',
      } as FileFormatterWithError);
    }
  });

  return { rows, rowLineNumbers, errors };
}

// Função que recebe um objeto FileFormatter e devolve um array com o nome de todos os campos que estão vazios
export function getEmptyFieldsOfferBulk(
  item: FileFormatter,
  optionalFields?: string[]
): string[] {
  if (item.ref_offer_id_csv) return [];

  const emptyFields: string[] = [];

  const arrayOfProps = Array.from(Object.entries(item));

  arrayOfProps.forEach(([key, value]) => {
    if (!value && (!optionalFields || !optionalFields.includes(key))) {
      emptyFields.push(key);
    }
  });

  return emptyFields;
}

export function generateAndDownloadCSV(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function mapOfferBulkHeaders(
  data: FileFormatter[],
  headerMap: HeaderMapConfig
): FileFormatter[] {
  return data.map(item => {
    const mappedItem: FileFormatter = {};

    Object.entries(item).forEach(([key, value]) => {
      const mappedKey = headerMap[sanitizeHeader(key)];
      if (!mappedKey) {
        return;
      }

      mappedItem[mappedKey] = String(value ?? '');
    });

    return mappedItem;
  });
}

export function createErrorCSVFromApiErrors(
  originalCSV: string,
  apiErrors: Record<string, string>,
  rowLineNumbers: number[]
): string {
  const csvLines = originalCSV
    .split('\n')
    .map(line => line.replace('\r', ''))
    .filter(Boolean);

  if (!csvLines.length) {
    return '';
  }

  const header = `${csvLines[0]},ERRO`;
  const errorRows = Object.entries(apiErrors)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([offerIndex, message]) => {
      const parsedOfferIndex = Number(offerIndex);
      const sourceLineNumber = rowLineNumbers[parsedOfferIndex];
      const sourceLine = csvLines[sourceLineNumber - 1] ?? '';
      const escapedMessage = `"${message.replace(/"/g, '""')}"`;
      return `${sourceLine},${escapedMessage}`;
    })
    .filter(Boolean);

  return [header, ...errorRows].join('\n');
}

function escapeCSVValue(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
}

export function createErrorCSVFromSubmittedOffers(
  submittedOffers: FileFormatter[],
  apiErrors: Record<string, string>,
  columns: string[]
): string {
  if (!submittedOffers.length || !columns.length) {
    return '';
  }

  const header = [...columns, 'ERRO'].join(',');
  const errorRows = Object.entries(apiErrors)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([offerIndex, message]) => {
      const submittedOffer = submittedOffers[Number(offerIndex)];
      if (!submittedOffer) return '';

      const rowValues = columns.map(column =>
        escapeCSVValue(String(submittedOffer[column] ?? ''))
      );
      const escapedMessage = escapeCSVValue(message);

      return [...rowValues, escapedMessage].join(',');
    })
    .filter(Boolean);

  return [header, ...errorRows].join('\n');
}
